import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Mail, MapPin, Phone, User, Globe2, Clock, ArrowUpRight } from "lucide-react";
import "./international.css";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const WORLD_WIDTH = 820;
const WORLD_HEIGHT = 520;
const WORLD_SCALE = 125; // Adjusted for equirectangular

// Topojson served from CDN — react-simple-maps fetches + parses it for us,
// so no `world-atlas` / `topojson-client` packages are required.
const WORLD_GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/**
 * Equirectangular projection, written out longhand so this file has ZERO
 * dependencies beyond react-simple-maps + lucide-react.
 *
 * It is the exact same formula d3-geo uses internally, which is what
 * <ComposableMap projection="geoEquirectangular" /> renders with — so our
 * markers land pixel-perfect on top of the drawn countries.
 *
 *   x = translateX + scale * (longitude in radians)
 *   y = translateY - scale * (latitude in radians)
 */
const DEG_TO_RAD = Math.PI / 180;

function projectEquirectangular([lon, lat]) {
  const x = WORLD_WIDTH / 2 + WORLD_SCALE * (lon * DEG_TO_RAD);
  const y = WORLD_HEIGHT / 2 - WORLD_SCALE * (lat * DEG_TO_RAD);
  return [x, y];
}

function toMarkerPercent([lon, lat]) {
  const [x, y] = projectEquirectangular([lon, lat]);
  return {
    x: `${(x / WORLD_WIDTH) * 100}%`,
    y: `${(y / WORLD_HEIGHT) * 100}%`,
    px: x,
    py: y,
  };
}

// Good spacing between European cities
const internationalOffices = [
  {
    key: "usa",
    country: "USA",
    region: "North America",
    timezone: "GMT −5:00",
    contact: "",
    address: "",
    phone: "",
    email: "",
    coordinates: [-74.006, 40.7128],
  },
  {
    key: "amsterdam",
    country: "Amsterdam",
    region: "Western Europe",
    timezone: "GMT +1:00",
    contact: "",
    address: "",
    phone: "",
    email: "",
    coordinates: [3.5, 53.1],
  },
  {
    key: "germany",
    country: "Germany",
    region: "Central Europe",
    timezone: "GMT +1:00",
    contact: "",
    address: "",
    phone: "",
    email: "",
    coordinates: [15.2, 51.7],
  },
  {
    key: "japan",
    country: "Japan",
    region: "East Asia",
    timezone: "GMT +9:00",
    contact: "",
    address: "",
    phone: "",
    email: "",
    coordinates: [139.6503, 35.6762],
  },
  {
    key: "london",
    country: "London (UK)",
    region: "United Kingdom",
    timezone: "GMT +0:00",
    contact: "",
    address: "",
    phone: "",
    email: "",
    coordinates: [-1.4, 50.6],
  },
].map((office) => ({ ...office, marker: toMarkerPercent(office.coordinates) }));

// India HQ — origin of every connection arc
const HQ = { name: "New Delhi (HQ)", coordinates: [77.209, 28.6139] };
const HQ_MARKER = toMarkerPercent(HQ.coordinates);

function mapSearchUrl(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/** Curved connector between HQ and an office, in projected screen space. */
function arcPath(from, to) {
  const mx = (from.px + to.px) / 2;
  const my = (from.py + to.py) / 2;
  const dx = to.px - from.px;
  const dy = to.py - from.py;
  const dist = Math.sqrt(dx * dx + dy * dy);
  // perpendicular lift so the arc bows upward
  const lift = Math.min(dist * 0.28, 110);
  const nx = -dy / (dist || 1);
  const ny = dx / (dist || 1);
  const cx = mx + nx * lift;
  const cy = my + ny * lift - Math.abs(dy) * 0.05;
  return `M ${from.px} ${from.py} Q ${cx} ${cy} ${to.px} ${to.py}`;
}

// ---------------------------------------------------------------------------
// WorldMap - Equirectangular Projection
// ---------------------------------------------------------------------------

function WorldMap({ selected, onSelect }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div
      className="group/map relative w-full overflow-hidden rounded-[24px] border border-[#FEF7F4] bg-gradient-to-br from-white via-[#FEF7F4] to-[#FAF7F2] shadow-[0_24px_60px_rgba(116,35,0,0.14)]"
      style={{ aspectRatio: `${WORLD_WIDTH} / ${WORLD_HEIGHT}` }}
    >
      {/* ambient glow blobs */}
      <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-[#F59C00]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-[#DA2128]/10 blur-3xl" />

      {/* shimmer skeleton — sits underneath the SVG while the topojson loads */}
      <div className="pointer-events-none absolute inset-0 z-0 assoc-shimmer" />

      <ComposableMap
        projection="geoEquirectangular"
        projectionConfig={{ scale: WORLD_SCALE }}
        width={WORLD_WIDTH}
        height={WORLD_HEIGHT}
        style={{ width: "100%", height: "100%", display: "block", position: "relative", zIndex: 10 }}
      >
        <defs>
          <linearGradient id="arcStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#DA2128" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#DB6400" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F59C00" stopOpacity="0.35" />
          </linearGradient>
          <filter id="arcGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <Geographies geography={WORLD_GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                tabIndex={-1}
                style={{
                  default: { fill: "#d6d6d8", stroke: "#ffffff", strokeWidth: 0.6, outline: "none", transition: "fill 260ms ease" },
                  hover: { fill: "#c2c2c6", stroke: "#ffffff", strokeWidth: 0.6, outline: "none" },
                  pressed: { fill: "#bdbdc2", stroke: "#ffffff", strokeWidth: 0.6, outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {/* animated connection arcs from HQ */}
        <g>
          {internationalOffices.map((office, i) => {
            const isActive = office.key === selected.key;
            return (
              <path
                key={`arc-${office.key}`}
                d={arcPath(HQ_MARKER, office.marker)}
                fill="none"
                stroke={isActive ? "url(#arcStroke)" : "#DB6400"}
                strokeWidth={isActive ? 2.1 : 1}
                strokeOpacity={isActive ? 1 : 0.28}
                strokeLinecap="round"
                strokeDasharray="7 9"
                filter={isActive ? "url(#arcGlow)" : undefined}
                className="assoc-arc"
                style={{ animationDelay: `${i * 0.35}s` }}
              />
            );
          })}
        </g>

        {/* HQ star marker */}
        <g transform={`translate(${HQ_MARKER.px}, ${HQ_MARKER.py})`}>
          <circle r="9" fill="#DA2128" opacity="0.16" className="assoc-hq-halo" />
          <circle r="4.6" fill="#DA2128" stroke="#fff" strokeWidth="1.6" />
        </g>
      </ComposableMap>

      {/* office markers */}
      {internationalOffices.map((office, i) => {
        const isActive = office.key === selected.key;
        const isHover = hovered === office.key;

        return (
          <button
            key={office.key}
            type="button"
            onClick={() => onSelect(office)}
            onMouseEnter={() => setHovered(office.key)}
            onMouseLeave={() => setHovered(null)}
            aria-label={`Show ${office.country} office`}
            className="absolute z-20 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all duration-300 hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DA2128] focus-visible:ring-offset-2"
            style={{ left: office.marker.x, top: office.marker.y }}
          >
            {/* ripple rings */}
            <span
              className={`absolute rounded-full ${isActive ? "h-9 w-9 bg-[#DA2128]/25" : "h-6 w-6 bg-[#F59C00]/20"} assoc-ripple`}
              style={{ animationDelay: `${i * 0.25}s` }}
            />
            {isActive && <span className="absolute h-7 w-7 animate-ping rounded-full bg-[#F59C00]/40" />}

            {/* gradient location dot */}
            <span
              className={`relative rounded-full border-[2px] border-white shadow-[0_6px_14px_rgba(119,61,0,0.35)] transition-all duration-300 ${
                isActive ? "h-[17px] w-[17px]" : isHover ? "h-[15px] w-[15px]" : "h-[13px] w-[13px]"
              }`}
              style={{
                backgroundImage:
                  "radial-gradient(circle at 32% 28%, #FFD277 0%, #F59C00 38%, #DB6400 66%, #DA2128 100%)",
              }}
            >
              <span className="absolute left-1/2 top-[18%] h-[3px] w-[4px] -translate-x-1/2 rounded-full bg-white/70" />
            </span>
          </button>
        );
      })}

      {/* HQ chip */}
      <div
        className="pointer-events-none absolute z-20 whitespace-nowrap rounded-[5px] border border-white/40 bg-[#DA2128] px-[8px] py-[3px] text-[9px] font-extrabold uppercase tracking-wide text-white shadow-[0_8px_16px_rgba(126,0,0,0.22)]"
        style={{ left: HQ_MARKER.x, top: HQ_MARKER.y, transform: "translate(-50%, 14px)" }}
      >
        India HQ
      </div>

      {/* selected label chip */}
      <div
        className="pointer-events-none absolute z-30 whitespace-nowrap rounded-[6px] bg-gradient-to-r from-[#F59C00] to-[#DB6400] px-[11px] py-[5px] text-[11px] font-extrabold uppercase leading-none tracking-wide text-white shadow-[0_10px_20px_rgba(126,56,0,0.28)] transition-all duration-500 ease-out"
        style={{
          left: selected.marker.x,
          top: selected.marker.y,
          transform: "translate(-50%, calc(-100% - 16px))",
        }}
      >
        {selected.country.toUpperCase()}
        <span className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[5px] border-x-transparent border-t-[#DB6400]" />
      </div>

      {/* legend */}
      <div className="pointer-events-none absolute bottom-3 left-4 z-30 flex items-center gap-4 rounded-full bg-white/80 px-3.5 py-1.5 text-[10px] font-semibold text-[#6a6a7a] shadow-sm backdrop-blur-sm">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#DA2128]" /> Headquarters</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#F59C00]" /> Overseas Office</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InternationalOfficeCard with Location Buttons
// ---------------------------------------------------------------------------

function InternationalOfficeCard({ office, offices, onSelect }) {
  const rows = [
    { icon: User, label: "Contact Person", value: office.contact, href: null },
    {
      icon: MapPin,
      label: "Address",
      value: office.address,
      href: office.address ? mapSearchUrl(office.address) : null,
      external: true,
    },
    {
      icon: Phone,
      label: "Phone",
      value: office.phone,
      href: office.phone ? `tel:${office.phone.replace(/[^0-9+]/g, "")}` : null,
    },
    { icon: Mail, label: "Email", value: office.email, href: office.email ? `mailto:${office.email}` : null },
  ];

  return (
    <div className="relative flex w-full flex-col overflow-hidden rounded-[24px] bg-gradient-to-br from-[#fb9a08] via-[#e97900] to-[#cb4a00] p-6 text-white shadow-[0_24px_60px_rgba(116,35,0,0.22)]">
      <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-14 -left-10 h-40 w-40 rounded-full bg-[#DA2128]/25 blur-2xl" />

      {/* Location Buttons */}
      <div className="relative z-10 mb-5 flex flex-wrap gap-1.5">
        {offices.map((loc) => (
          <button
            key={loc.key}
            onClick={() => onSelect(loc)}
            className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition-all duration-300 ${
              loc.key === office.key
                ? "scale-105 bg-white text-[#c67800] shadow-md"
                : "bg-white/20 text-white hover:bg-white/35"
            }`}
          >
            {loc.country}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="relative z-10 mb-5 border-b border-white/25 pb-4">
        <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/70">
          <Globe2 className="h-3 w-3" /> {office.region}
        </div>
        <h4 className="font-['Cormorant_Garamond'] text-3xl font-semibold leading-none tracking-tight">
          {office.country}
        </h4>
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-semibold">
          <Clock className="h-3 w-3" /> {office.timezone}
        </div>
      </div>

      {/* Details */}
      <div className="relative z-10 space-y-3 text-[13px] leading-[1.4]">
        {rows.map(({ icon: Icon, label, value, href, external }) => {
          const content = (
            <>
              <span className="mt-[1px] flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 transition-colors group-hover:bg-white/35">
                <Icon className="h-3.5 w-3.5 stroke-[2.3]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[9.5px] font-bold uppercase tracking-wider text-white/60">{label}</span>
                <span className="block break-words group-hover:underline">{value || "—"}</span>
              </span>
              {href && <ArrowUpRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 opacity-70" />}
            </>
          );

          return href ? (
            <a
              key={label}
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noreferrer" : undefined}
              className="group flex gap-2.5 rounded-lg outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/80"
            >
              {content}
            </a>
          ) : (
            <div key={label} className="group flex gap-2.5">
              {content}
            </div>
          );
        })}
      </div>

      <a
        href="#"
        className="relative z-10 mt-6 block rounded-full bg-white py-3 text-center text-[13px] font-bold text-[#c64a00] shadow-lg transition-transform hover:scale-[1.02]"
      >
        Connect with this Office →
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function GlobalPresence() {
  const [selectedKey, setSelectedKey] = useState("usa");

  const selectedOffice = useMemo(
    () => internationalOffices.find((office) => office.key === selectedKey) || internationalOffices[0],
    [selectedKey]
  );

  const handleSelect = (office) => setSelectedKey(office.key);

  return (
    <div className="rounded-[28px] border border-[#FEF7F4] bg-white p-6 shadow-xl shadow-black/[0.04] md:p-10">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#DA2128]/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-[#DA2128]">
            <MapPin className="h-3.5 w-3.5" /> Global Footprint
          </div>
          <h3 className="font-['Cormorant_Garamond'] text-4xl font-semibold tracking-tight text-[#1a1a2e] md:text-5xl">
            ASSOCHAM <span className="italic font-normal text-[#DB6400]">International Offices</span>
          </h3>
          <p className="mt-3 max-w-xl text-[#6a6a7a]">
            Select a location on the map to view office details, contacts, and regional coverage.
          </p>
        </div>
        <div className="flex gap-6">
          <div>
            <div className="font-['Outfit'] text-3xl font-bold text-[#DA2128]">05</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#9a9aab]">Overseas Offices</div>
          </div>
          <div>
            <div className="font-['Outfit'] text-3xl font-bold text-[#DB6400]">04</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#9a9aab]">Continents</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-stretch gap-6 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <WorldMap selected={selectedOffice} onSelect={handleSelect} />
        </div>
        <div className="w-full lg:w-[320px] lg:flex-shrink-0">
          <InternationalOfficeCard office={selectedOffice} offices={internationalOffices} onSelect={handleSelect} />
        </div>
      </div>
    </div>
  );
}
